// src/components/GuidedJournalChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PERSONAS_DATA } from '../data/personas_data';
import PersonaModal from './PersonaModal';
import { supabase } from '../supabaseClient';
import { RichTextEditor } from "./RichTextEditor";
import { sanitizeRichHtml, sanitizeRichHtmlOrPlainText, stripHtmlToText, escapeHtmlText } from "../lib/richText";

import { SendIcon, RotateCcw } from "lucide-react";

const GUIDED_DRAFT_KEY = "innergem_guided_satir_draft_v2";

const persistGuidedDraft = (payload) => {
  try {
    localStorage.setItem(GUIDED_DRAFT_KEY, JSON.stringify(payload));
  } catch (e) {
    console.warn("Failed to persist guided draft:", e);
  }
};

const GuidedJournalChat = ({ onComplete, maxLimit = 1500 }) => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0); // 0-based, 对应第 1 步
  const [answers, setAnswers] = useState({}); // { stepIndex: { answer: string } }
  const [currentInput, setCurrentInput] = useState(""); // HTML
  const [currentInputPlain, setCurrentInputPlain] = useState("");
  const [selectedPersonaKey, setSelectedPersonaKey] = useState(null);
  const [aiPrompts, setAiPrompts] = useState({}); // { [stepIndex]: string } 赛博禅师提问/引导语
  const [isSending, setIsSending] = useState(false);

  const scrollContainerRef = useRef(null);
  const promptBubbleRefs = useRef({}); // { [idx]: HTMLElement }
  const lastAutoScrollKeyRef = useRef("");

  const handleInputChange = (nextHtml, nextPlain) => {
    setCurrentInput(nextHtml);
    setCurrentInputPlain(nextPlain);
    persistGuidedDraft({ currentStep, answers, aiPrompts, currentInput: nextHtml });
  };

  // 请求某一步的赛博禅师引导语（AI 提示），并存入 aiPrompts
  const fetchStepPrompt = async (stepIndex, userLatestInput, snapshots) => {
    // Edge Function 会对空输入返回 400；Step 1 的问题我们用本地固定文案即可
    if (!String(userLatestInput || "").trim()) {
      return;
    }

    setIsSending(true);
    try {
      const answersSnapshot = snapshots?.answers || answers;
      const aiPromptsSnapshot = snapshots?.aiPrompts || aiPrompts;

      // 构造到目前为止的 Q/A 历史，便于后端上下文理解
        const historyList = [];
      for (let i = 0; i < stepIndex - 1; i++) {
        const prevPrompt = aiPromptsSnapshot[i] || "";
          const prevAnswer = answersSnapshot[i]?.answerText || "";
        if (prevPrompt || prevAnswer) {
          historyList.push(`Q: ${prevPrompt}\nA: ${prevAnswer}`);
        }
      }

      const appLang = (i18n && i18n.language) || navigator.language || 'en';
      const userLangForApi = appLang.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';

      const { data, error } = await supabase.functions.invoke('guided-satir-step', {
        body: {
          stepIndex,
          chatHistory: historyList,
          userLatestInput: userLatestInput || "",
          userLanguage: userLangForApi,
        },
      });

      if (!error && data && data.replyText) {
        const nextPrompts = {
          ...aiPromptsSnapshot,
          [stepIndex - 1]: data.replyText,
        };

        setAiPrompts((prev) => ({
          ...prev,
          [stepIndex - 1]: data.replyText, // 0-based 索引
        }));

        // Persist immediately so the latest AI prompt isn't lost on fast navigation/app close.
        persistGuidedDraft({
          currentStep,
          answers: answersSnapshot,
          aiPrompts: nextPrompts,
          currentInput,
        });
      }
    } catch (e) {
      console.warn('guided-satir-step prompt error:', e);
    } finally {
      setIsSending(false);
    }
  };

  // 初始加载：先尝试从 localStorage 读草稿，没有就从第 1 步开始
  useEffect(() => {
    try {
      const raw = localStorage.getItem(GUIDED_DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved) {
          const savedCurrentStep = saved.currentStep || 0;
          const savedAnswers = saved.answers || {};
          const savedPrompts = saved.aiPrompts || {};

          // Step 1 的「问」是固定文案：如果缺失就用本地文案补上，避免初始空白
          if (!String(savedPrompts?.[0] || "").trim()) {
            savedPrompts[0] = t('guided.firstStepPlaceholder', {
              defaultValue:
                '先用几句话写下今天最让你难受、最卡住的那个瞬间：发生了什么？有哪些人出现？那一刻你最直接的感受是什么？',
            });
          }

          setCurrentStep(savedCurrentStep);
          setAnswers(savedAnswers);
          setAiPrompts(savedPrompts);
          const restoredInput = String(saved.currentInput || "");
          const restoredHtml = sanitizeRichHtmlOrPlainText(restoredInput);
          const restoredPlain = stripHtmlToText(restoredHtml);
          setCurrentInput(restoredHtml);
          setCurrentInputPlain(restoredPlain.length <= maxLimit ? restoredPlain : restoredPlain.slice(0, maxLimit));

          // 为了让最新的后端提示生效：仅在“当前步 prompt 缺失”且“上一题答案存在”时才拉取
          // currentStep 是下一题（未回答）的索引，因此上一题答案在 currentStep - 1
          const stepForApi = savedCurrentStep + 1; // API 是 1-based
          const prevAnswer = savedCurrentStep > 0
            ? (savedAnswers?.[savedCurrentStep - 1]?.answerText || "")
            : "";
          const currentPromptMissing = !String(savedPrompts?.[savedCurrentStep] || "").trim();
          if (currentPromptMissing && String(prevAnswer || "").trim()) {
            fetchStepPrompt(stepForApi, prevAnswer, { answers: savedAnswers, aiPrompts: savedPrompts });
          }
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to restore guided draft:", e);
    }

    // 新开始：把 Step 1 的固定问题写入气泡（不调用后端）
    setAiPrompts((prev) => {
      if (String(prev?.[0] || "").trim()) return prev;
      return {
        ...prev,
        0: t('guided.firstStepPlaceholder', {
          defaultValue:
            '先用几句话写下今天最让你难受、最卡住的那个瞬间：发生了什么？有哪些人出现？那一刻你最直接的感受是什么？',
        }),
      };
    });
  }, []);

  // 每次状态变化就自动保存到 localStorage
  useEffect(() => {
    const payload = {
      currentStep,
      answers,
      aiPrompts,
      currentInput,
    };
    persistGuidedDraft(payload);
  }, [currentStep, answers, aiPrompts, currentInput]);

  // 用户发送后：保持滚动到底部，让用户看到自己刚发出的内容（内部 scrollbar）
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [currentStep, answers]);

  // AI 回复后：滚动到「AI 回复气泡的第一行」
  useEffect(() => {
    const prompt = aiPrompts?.[currentStep];
    if (!String(prompt || "").trim()) return;

    const key = `${currentStep}:${String(prompt).slice(0, 120)}`;
    if (lastAutoScrollKeyRef.current === key) return;
    lastAutoScrollKeyRef.current = key;

    const bubble = promptBubbleRefs.current?.[currentStep];
    if (!bubble || typeof bubble.scrollIntoView !== "function") return;

    // Ensure DOM has measured layout
    requestAnimationFrame(() => {
      try {
        bubble.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
      } catch {
        // ignore
      }
    });
  }, [currentStep, aiPrompts]);

  // 点击人格 chip 打开弹窗 + 解锁
  const handlePersonaClick = (personaName) => {
    const entry = Object.entries(PERSONAS_DATA).find(
      ([, p]) => p.englishName === personaName || p.name === personaName
    );
    if (!entry) return;

    const [key, persona] = entry;
    setSelectedPersonaKey(key);

    const currentUnlocked = JSON.parse(
      localStorage.getItem("innergem_unlocked_personas") || "[]"
    );
    if (!currentUnlocked.includes(persona.name)) {
      const next = [...currentUnlocked, persona.name];
      localStorage.setItem(
        "innergem_unlocked_personas",
        JSON.stringify(next)
      );
    }
  };

  // 把问题里的 persona 名字变成按钮（同 Save 按钮紫色系）
  const renderMessageText = (text) => {
    const appLang = (i18n && i18n.language) || navigator.language || 'en';
    const isZh = String(appLang).toLowerCase().startsWith('zh');

    const entries = Object.entries(PERSONAS_DATA || {});
    const aliasToKey = new Map();

    // Build exact-match aliases from config
    for (const [key, p] of entries) {
      if (!p) continue;
      if (p.name) aliasToKey.set(String(p.name), key);
      if (p.englishName) {
        const en = String(p.englishName);
        aliasToKey.set(en, key);
        // Common variant: without leading "The "
        aliasToKey.set(en.replace(/^The\s+/i, ""), key);
      }
    }

    // Heuristic matching for bracketed aliases that are close but not exact
    const guessPersonaKey = (raw) => {
      const direct = aliasToKey.get(raw);
      if (direct) return direct;

      const lower = String(raw || "").trim().toLowerCase();
      if (!lower) return null;

      // Map common Gemini variants (e.g., "Invisible Person") to canonical keys
      const keywordToEnglishName = [
        ["invisible", "The Invisible"],
        ["sinner", "The Sinner"],
        ["bystander", "The Bystander"],
        ["victim", "The Victim"],
        ["coward", "The Coward"],
        ["addict", "The Addict"],
        ["tyrant", "The Tyrant"],
        ["performer", "The Performer"],
        ["actor", "The Performer"],
        ["explorer", "The Explorer"],
        ["doer", "The Doer"],
        ["sage", "The Sage"],
        ["healer", "The Healer"],
        ["celebrator", "The Celebrator"],
        ["peaceful observer", "The Peaceful Observer"],
        ["observer", "The Peaceful Observer"],
        ["enlightened", "The Enlightened One"],
      ];

      for (const [kw, canonicalEn] of keywordToEnglishName) {
        if (lower.includes(kw)) {
          const k = aliasToKey.get(canonicalEn);
          if (k) return k;
        }
      }

      return null;
    };

    const canonicalDisplayNameForKey = (key) => {
      const p = PERSONAS_DATA?.[key];
      if (!p) return null;
      return isZh ? p.name : p.englishName;
    };

    const normalizePersonaMentions = (input) => {
      const s = String(input || "");
      if (!s) return s;

      // Normalize bracketed persona tags to canonical display names.
      // Supports both [[...]] and 【...】.
      return s.replace(/\[\[([^\]]{1,60})\]\]|【([^】]{1,60})】/g, (full, m1, m2) => {
        const inner = (m1 || m2 || "").trim();
        const key = guessPersonaKey(inner);
        if (!key) return full;
        const canonical = canonicalDisplayNameForKey(key);
        if (!canonical) return full;

        // Keep original bracket style
        if (full.startsWith("[[")) return `[[${canonical}]]`;
        return `【${canonical}】`;
      });
    };

    const normalizedText = normalizePersonaMentions(text);

    const names = Array.from(
      new Set(
        Object.values(PERSONAS_DATA)
          .flatMap((p) => [p.englishName, p.name])
          .filter(Boolean)
      )
    );
    if (!names.length) {
      return <span className="whitespace-pre-wrap">{normalizedText}</span>;
    }

    const escaped = names
      .sort((a, b) => b.length - a.length)
      .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const alternation = escaped.join("|");
    // Capture the name itself so split keeps it; allow optional surrounding quotes
    const pattern = new RegExp(`["'“”»«]?(${alternation})["'“”»«]?`, "gu");

    const parts = normalizedText.split(pattern);
    if (!parts || parts.length === 0) {
      return <span className="whitespace-pre-wrap">{normalizedText}</span>;
    }

    return parts.map((part, index) => {
      const clean = (part || "").replace(/^["'“”»«]|["'“”»«]$/g, "");
      const isPersona = names.includes(clean);
      if (!isPersona) {
        return (
          <span key={index} className="whitespace-pre-wrap">
            {part}
          </span>
        );
      }
      return (
        <button
          key={index}
          type="button"
          onClick={() => handlePersonaClick(clean)}
          className="inline-flex items-center px-2 py-0.5 mx-1 rounded-md border cursor-pointer align-middle text-xs font-semibold transition-all bg-primary/10 text-primary border-primary/30 hover:bg-primary hover:text-primary-foreground hover:shadow-sm active:scale-95"
        >
          {clean}
        </button>
      );
    });
  };

  // 组合所有步骤答案成一篇日记
  const generateJournalEntry = (answersData) => {
    let entry = `<div><strong>${escapeHtmlText(
      t('guided.exportHeader', { defaultValue: '--- Satir Guided Journal Entry ---' })
    )}</strong></div><br/>`;
    Object.keys(answersData)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((key) => {
        const idx = Number(key);
        const item = answersData[key];
        let prompt = aiPrompts[idx] || '';
        // Step 1 的「问」是固定文案：沿用输入框里的第一步引导文案
        if (idx === 0 && !prompt.trim()) {
          prompt = t('guided.firstStepPlaceholder', {
            defaultValue:
              '先用几句话写下今天最让你难受、最卡住的那个瞬间：发生了什么？有哪些人出现？那一刻你最直接的感受是什么？',
          });
        }
        const qLabel = t('guided.qLabel', { defaultValue: 'Q:' });
        const aLabel = t('guided.aLabel', { defaultValue: 'A:' });
        const safePrompt = escapeHtmlText(prompt);
        const answerHtml = sanitizeRichHtml(item?.answerHtml || "");

        entry += `<div><strong>${escapeHtmlText(`Step ${idx + 1}`)}</strong></div>`;
        entry += `<div><strong>${escapeHtmlText(qLabel)}</strong> ${safePrompt}</div>`;
        entry += `<div><strong>${escapeHtmlText(aLabel)}</strong></div>`;
        entry += `<div>${answerHtml || escapeHtmlText(item?.answerText || "")}</div><br/>`;
    });
    return entry;
  };

  const handleSend = async () => {
    const sanitizedHtml = sanitizeRichHtml(currentInput);
    const userAnswerText = stripHtmlToText(sanitizedHtml).trim();
    if (!userAnswerText) return;
    if (userAnswerText.length > maxLimit) return;

    const userAnswerHtml = sanitizedHtml.trim();

    const stepIndex = currentStep; // 0-based

    const nextAnswers = {
      ...answers,
      [stepIndex]: {
        answerHtml: userAnswerHtml,
        answerText: userAnswerText,
      },
    };
    setAnswers(nextAnswers);
    setCurrentInput("");
    setCurrentInputPlain("");

    // Persist immediately so user's last message isn't lost on fast navigation/app close.
    persistGuidedDraft({
      currentStep: currentStep + 1,
      answers: nextAnswers,
      aiPrompts,
      currentInput: "",
    });

    // 每次用户提交回答时，都生成当前进度的日记文本并回传，
    // 这样即使没走完 10 步也可以保存已有内容
    if (onComplete) {
      const partialEntry = generateJournalEntry(nextAnswers);
      onComplete(partialEntry);
    }

    // 进入下一题
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    // 根据当前回答拉取下一步的 AI 引导
    await fetchStepPrompt(nextStep + 1, userAnswerText, { answers: nextAnswers, aiPrompts });
  };

  const handleRestart = () => {
    if (
      !window.confirm(
        t('guided.restartConfirm')
      )
    ) {
      return;
    }
    localStorage.removeItem(GUIDED_DRAFT_KEY);
    setCurrentStep(0);
    setAnswers({});
    setAiPrompts({});
    setCurrentInput("");
    setCurrentInputPlain("");
    if (onComplete) onComplete("");
  };
  const inputPlaceholder = currentStep === 0 && !answers[0]
    ? t('guided.firstStepInputPlaceholder', {
        defaultValue: '在这里写下你的心情吧',
      })
    : t('guided.inputPlaceholder', {
        defaultValue:
          '顺着刚才的话继续写下去。如果问题不完全贴合，也可以改写成此刻最真实在你心里的东西。按 Enter 发送，Shift+Enter 换行。',
      });

  return (
    <div className="flex flex-col bg-white/80 rounded-2xl border border-purple-100/70 shadow-sm px-4 py-3">
      {/* 顶部：标题 + 进度 + restart */}
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="text-[13px] font-semibold text-purple-700">
          {t('guided.title')}
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleRestart}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-purple-600 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            {t('guided.restart')}
          </button>
        </div>
      </div>

      {/* 中间：固定高度 + 内部滚动的提问区（紫色 Q&A 气泡） */}
      <div
        ref={scrollContainerRef}
        className="rounded-2xl bg-white border border-purple-50 px-4 py-3 overflow-y-auto space-y-4"
        style={{ maxHeight: 260, minHeight: 260 }}   // <== 强制内部高度
      >
        {Array.from({ length: currentStep + 1 }).map((_, idx) => {
          const prompt = aiPrompts[idx] || '';
          const answerObj = answers[idx];

          return (
            <div key={idx} className="flex flex-col gap-2">
              {/* 赛博禅师提问气泡：左侧，浅紫色 */}
              {prompt && (
                <div className="flex justify-start">
                  <div
                    ref={(el) => {
                      if (el) promptBubbleRefs.current[idx] = el;
                      else delete promptBubbleRefs.current[idx];
                    }}
                    className="inline-block max-w-[82%] rounded-2xl bg-primary/5 px-4 py-3 text-sm leading-relaxed text-primary"
                  >
                    {renderMessageText(prompt)}
                  </div>
                </div>
              )}

              {/* 回答气泡：右侧，深紫色 */}
              {answerObj && (
                <>
                  <div className="flex justify-end">
                    <div
                      className="inline-block max-w-[75%] rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground shadow-sm break-words"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(answerObj.answerHtml || ""),
                      }}
                    />
                  </div>

                  {/* 不再单独渲染橙色 AI 泡泡，改为上面的紫色提问气泡 */}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* 底部：输入栏 + Send + 字数（字数在按钮下面） */}
      <div className="mt-3 flex items-end gap-3 justify-between">
        {/* 多行输入框：高一点 + 内部滚动 */}
        <div className="flex-1">
          <RichTextEditor
            valueHtml={currentInput}
            onChange={handleInputChange}
            placeholder={inputPlaceholder}
            disabled={isSending}
            maxLength={maxLimit}
            minHeightClassName="min-h-[90px] max-h-[160px] overflow-y-auto"
            submitOnEnter
            onSubmit={handleSend}
          />
        </div>

        {/* 右侧：Send + 字数 */}
        <div className="flex flex-col items-center gap-1">
          <button
            type="button"
            onClick={handleSend}
            disabled={!currentInputPlain.trim() || isSending}
            className="flex h-10 w-10 items-center justify-center rounded-full
                       bg-primary text-primary-foreground shadow-sm
                       disabled:opacity-40 disabled:cursor-not-allowed
                       hover:bg-primary/90 transition-all"
          >
            {isSending ? (
              <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
            ) : (
              <SendIcon size={18} />
            )}
          </button>
          <span className="text-[11px] text-muted-foreground">
            {currentInputPlain.length}/{maxLimit}
          </span>
        </div>
      </div>

      {/* Persona 详情弹窗 */}
      {selectedPersonaKey && PERSONAS_DATA[selectedPersonaKey] && (
        <PersonaModal
          persona={PERSONAS_DATA[selectedPersonaKey]}
          onClose={() => setSelectedPersonaKey(null)}
          isUnlocked={true}
        />
      )}
    </div>
  );
};

export default GuidedJournalChat;
